import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import {
  accentMaterial,
  bodyMaterial,
  createMotes,
  createPedestal,
  glassMaterial,
  glowMaterial,
  PortalScene,
  SceneFactory,
} from './portal-scene';

const BAR_BASE = [0.55, 1.0, 0.78, 1.42, 1.08];

/** Dashboard: a glass analytics panel with live bars and a donut chart. */
export const createDashboardScene: SceneFactory = (accent): PortalScene => {
  const root = new THREE.Group();

  const pivot = new THREE.Group();
  root.add(pivot);

  const panel = new THREE.Mesh(new RoundedBoxGeometry(3.1, 2.1, 0.12, 6, 0.1), glassMaterial(accent));
  panel.position.set(0, 0.25, -0.5);
  panel.rotation.x = -0.06;
  pivot.add(panel);

  const deck = new THREE.Mesh(new RoundedBoxGeometry(2.9, 0.14, 1.1, 5, 0.06), bodyMaterial(0x2c3870));
  deck.position.set(0, -0.92, 0.1);
  pivot.add(deck);

  const barGeometry = new RoundedBoxGeometry(0.34, 1, 0.34, 5, 0.08);
  const bars = BAR_BASE.map((height, index) => {
    const bar = new THREE.Mesh(barGeometry, accentMaterial(accent, index % 2 === 0 ? 0.55 : 0.28));
    bar.position.set(-1.08 + index * 0.54, 0, 0.1);
    bar.scale.y = height;
    pivot.add(bar);
    return bar;
  });

  const donut = new THREE.Mesh(new THREE.TorusGeometry(0.44, 0.17, 28, 72), accentMaterial(accent, 0.7));
  donut.position.set(1.06, 0.95, 0.45);
  pivot.add(donut);

  const donutCore = new THREE.Mesh(
    new THREE.TorusGeometry(0.44, 0.175, 28, 72, Math.PI * 0.6),
    bodyMaterial(0x263163),
  );
  donutCore.position.copy(donut.position);
  pivot.add(donutCore);

  const needle = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 1.9, 12), glowMaterial(accent, 0.75));
  needle.position.set(-0.75, 1.0, 0.4);
  needle.rotation.z = -0.55;
  pivot.add(needle);

  const motes = createMotes(accent, 14, 2.1);
  root.add(motes, createPedestal(accent));

  return {
    root,
    update(elapsed, hover) {
      pivot.position.y = Math.sin(elapsed * 1.05) * 0.07 + hover * 0.1;

      bars.forEach((bar, index) => {
        const wave = Math.sin(elapsed * (1.5 + index * 0.35) + index) * (0.22 + hover * 0.22);
        const height = Math.max(0.22, BAR_BASE[index] + wave);
        bar.scale.y = height;
        bar.position.y = -0.85 + height / 2;
      });

      donut.rotation.z = elapsed * (0.5 + hover * 1.3);
      donutCore.rotation.z = elapsed * (0.5 + hover * 1.3);
      needle.rotation.z = -0.55 + Math.sin(elapsed * 0.9) * 0.18;
      motes.rotation.y = -elapsed * 0.16;
    },
  };
};
